import { useState, useEffect, useMemo } from "react";
import {
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiTrendingUp,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiEdit3,
  FiChevronRight,
  FiChevronLeft,
  FiTrash2,
} from "react-icons/fi";
import { getUsers, getClients } from "../api/api";
import { getPayrollsByMonth } from "../api/api";
import { getExpensesByDateRange } from "../api/api";
import {
  createMonthlyReport,
  getMonthlyReports,
  getMonthlyReportByMonth,
  deleteMonthlyReport,
  updateMonthlyReport,
} from "../api/api";
import toast from "react-hot-toast";
import "./MonthlyReport.css";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const monthLabel = (m) => MONTHS.find((x) => x.value === Number(m))?.label || "—";

const now = new Date();
const currentMonthNum = now.getMonth() + 1;
const currentYear = now.getFullYear();

const lastDayOfMonth = (month, year) => new Date(year, month, 0).getDate();
const dateRangeForMonth = (month, year) => {
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const to = `${year}-${String(month).padStart(2, "0")}-${String(lastDayOfMonth(month, year)).padStart(2, "0")}`;
  return { from, to };
};

const formatEGP = (n) => `${(Number(n) || 0).toLocaleString("en-US")} EGP`;
const formatNum = (n) => (Number(n) || 0).toLocaleString("en-US");

const EMPTY_OVERRIDES = {
  totalRevenue: "",
  totalExpenses: "",
  totalSalaryCost: "",
  activeClients: "",
  collectedPayments: "",
  pendingPayments: "",
  employeeCount: "",
};

const OVERRIDE_FIELDS = [
  { key: "totalRevenue", label: "Total Revenue", type: "currency" },
  { key: "totalExpenses", label: "Total Expenses", type: "currency" },
  { key: "totalSalaryCost", label: "Total Salary Cost", type: "currency" },
  { key: "activeClients", label: "Active Clients", type: "count" },
  { key: "collectedPayments", label: "Collected Payments", type: "currency" },
  { key: "pendingPayments", label: "Pending Payments", type: "currency" },
  { key: "employeeCount", label: "Employee Count", type: "count" },
];

const STEPS = [
  { n: 1, label: "Month & Year", icon: FiCalendar },
  { n: 2, label: "Financial Summary", icon: FiDollarSign },
  { n: 3, label: "Attendance", icon: FiUsers },
  { n: 4, label: "Notes", icon: FiFileText },
  { n: 5, label: "Review", icon: FiCheckCircle },
];

function MetricCard({ icon: Icon, label, value, sub, accent = "gold", badge }) {
  return (
    <div className={`mr-metric-card accent-${accent}`}>
      <div className="mr-metric-top">
        <span className="mr-metric-icon"><Icon size={18} /></span>
        {badge && <span className={`mr-badge ${badge}`}>{badge === "auto" ? "Auto" : "Manual"}</span>}
      </div>
      <div className="mr-metric-label">{label}</div>
      <div className="mr-metric-value">{value}</div>
      {sub && <div className="mr-metric-sub">{sub}</div>}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mr-section-title">
      <span className="mr-section-icon"><Icon size={16} /></span>
      <div>
        <h4>{title}</h4>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
  );
}

export default function MonthlyReport() {
  const [step, setStep] = useState(1);
  const [month, setMonth] = useState(currentMonthNum);
  const [year, setYear] = useState(currentYear);

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [existingReportWarning, setExistingReportWarning] = useState(false);

  const [attendance, setAttendance] = useState([]);
  const [risksAndDecisions, setRisksAndDecisions] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideFields, setOverrideFields] = useState(EMPTY_OVERRIDES);

  const [pastReports, setPastReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [viewingReport, setViewingReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  useEffect(() => {
    getUsers()
      .then((d) => setUsers(Array.isArray(d?.data ?? d) ? (d.data ?? d) : []))
      .catch(() => {});
  }, []);

  const fetchPastReports = async () => {
    try {
      setLoadingReports(true);
      const res = await getMonthlyReports();
      const list = res?.data?.data ?? res?.data ?? res ?? [];
      setPastReports(Array.isArray(list) ? list : []);
    } catch {
      // no reports yet is fine
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => { fetchPastReports(); }, []);

  const userName = (id) => {
    const u = users.find((x) => x._id === (id?._id || id));
    return u?.userName || u?.name || "Unknown";
  };

  const calcSalaryCost = payrolls.reduce((s, p) => s + (Number(p.netSalary) || 0), 0);
  const calcExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const calcEmployeeCount = payrolls.length;
  const calcActiveClients = clients.filter((c) => c.status === "active").length;

  const localDefaults = useMemo(() => ({
    totalSalaryCost: calcSalaryCost,
    totalExpenses: calcExpenses,
    employeeCount: calcEmployeeCount,
    activeClients: calcActiveClients,
    totalRevenue: null,
    collectedPayments: null,
    pendingPayments: null,
  }), [calcSalaryCost, calcExpenses, calcEmployeeCount, calcActiveClients]);

  const effective = useMemo(() => {
    const pick = (key, localVal) => {
      const raw = overrideFields[key];
      if (raw !== "" && raw != null) return Number(raw);
      return localVal;
    };
    const salary = pick("totalSalaryCost", calcSalaryCost);
    const exp = pick("totalExpenses", calcExpenses);
    const employees = pick("employeeCount", calcEmployeeCount);
    const active = pick("activeClients", calcActiveClients);
    const revenue = overrideFields.totalRevenue !== "" ? Number(overrideFields.totalRevenue) : null;
    const collected = overrideFields.collectedPayments !== "" ? Number(overrideFields.collectedPayments) : null;
    const pending = overrideFields.pendingPayments !== "" ? Number(overrideFields.pendingPayments) : null;
    const netProfit = revenue != null ? revenue - exp - salary : null;
    return { salary, exp, employees, active, revenue, collected, pending, netProfit };
  }, [overrideFields, calcSalaryCost, calcExpenses, calcEmployeeCount, calcActiveClients]);

  const overrideCount = Object.values(overrideFields).filter((v) => v !== "").length;

  const handleLoadData = async () => {
    try {
      setLoadingData(true);
      setExistingReportWarning(false);
      const { from, to } = dateRangeForMonth(month, year);

      const [payrollRes, expenseRes, clientsRes, existingRes] = await Promise.all([
        getPayrollsByMonth(month, year, 1, 200),
        getExpensesByDateRange(from, to, 1, 200),
        getClients().catch(() => ({ data: [] })),
        getMonthlyReportByMonth(month, year).catch(() => ({ data: null })),
      ]);

      const payrollList = payrollRes?.data?.data ?? payrollRes?.data ?? [];
      const expenseList = expenseRes?.data?.data ?? expenseRes?.data ?? [];
      const clientsList = clientsRes?.data?.data ?? clientsRes?.data ?? clientsRes ?? [];
      const existing = existingRes?.data?.data ?? existingRes?.data ?? existingRes;

      console.log("Payroll API response:", payrollRes);
      console.log("Payroll list for month", month, "year", year, ":", payrollList);

      // Filter payroll records by month and year (client-side fallback)
      const filteredPayrollList = (Array.isArray(payrollList) ? payrollList : []).filter(p => {
        const pMonth = Number(p.month);
        const pYear = Number(p.year);
        return pMonth === Number(month) && pYear === Number(year);
      });
      console.log("Filtered payroll list:", filteredPayrollList);

      setPayrolls(Array.isArray(filteredPayrollList) ? filteredPayrollList : []);
      setExpenses(Array.isArray(expenseList) ? expenseList : []);
      setClients(Array.isArray(clientsList) ? clientsList : []);
      setExistingReportWarning(!!existing?._id);

      const workDaysDefault = 22;
      const attendanceRows = (Array.isArray(filteredPayrollList) ? filteredPayrollList : []).map((p) => ({
        employee: p.userId?._id || p.userId,
        workDays: workDaysDefault,
        absentDays: Number(p.absences) || 0,
        annualLeaves: 0,
        sickLeaves: 0,
        lateCount: 0,
        notes: null,
      }));
      // Remove duplicates based on employee ID
      const uniqueAttendance = attendanceRows.filter((row, index, self) =>
        index === self.findIndex((r) => r.employee === row.employee)
      );
      console.log("Attendance rows created:", attendanceRows);
      console.log("Unique attendance rows:", uniqueAttendance);
      setAttendance(uniqueAttendance);

      setOverrideFields(EMPTY_OVERRIDES);
      setShowOverrides(false);
      setDataLoaded(true);
      setStep(2);
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || "Failed to load data for this month");
    } finally {
      setLoadingData(false);
    }
  };

  const updateAttendanceField = (index, field, value) => {
    setAttendance((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const updateOverrideField = (field, value) => {
    setOverrideFields((prev) => ({ ...prev, [field]: value }));
  };

  const resetOverride = (key) => updateOverrideField(key, "");

  const conflictMessage = () =>
    `A monthly report already exists for ${monthLabel(month)} ${year}. Only one report per month is allowed.`;

  const buildPayload = () => {
    const payload = {
      month: Number(month),
      year: Number(year),
      attendance,
      risksAndDecisions: risksAndDecisions.trim() || undefined,
      recommendations: recommendations.trim() || undefined,
    };
    OVERRIDE_FIELDS.forEach(({ key }) => {
      if (overrideFields[key] !== "") payload[key] = Number(overrideFields[key]);
    });
    return payload;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await createMonthlyReport(buildPayload());
      toast.success("Monthly report created successfully");
      setStep(1);
      setDataLoaded(false);
      setPayrolls([]);
      setExpenses([]);
      setClients([]);
      setAttendance([]);
      setRisksAndDecisions("");
      setRecommendations("");
      setOverrideFields(EMPTY_OVERRIDES);
      setShowOverrides(false);
      setExistingReportWarning(false);
      fetchPastReports();
    } catch (err) {
      const status = err?.status || err?.response?.status;
      if (status === 409) toast.error(conflictMessage());
      else toast.error(err?.message || err?.response?.data?.message || "Failed to create report");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReport = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    try {
      await deleteMonthlyReport(id);
      setPastReports((prev) => prev.filter((r) => r._id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || "Failed to delete");
    }
  };

  const handleEditReport = (report, e) => {
    e.stopPropagation();
    setEditingReport(report);
  };

  const handleUpdateReport = async () => {
    try {
      setSubmitting(true);
      const payload = {
        attendance: editingReport.attendance,
        risksAndDecisions: editingReport.risksAndDecisions?.trim() || undefined,
        recommendations: editingReport.recommendations?.trim() || undefined,
      };
      OVERRIDE_FIELDS.forEach(({ key }) => {
        if (editingReport[key] != null) payload[key] = Number(editingReport[key]);
      });
      await updateMonthlyReport(editingReport._id, payload);
      toast.success("Report updated successfully");
      setEditingReport(null);
      fetchPastReports();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || "Failed to update report");
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (n) => {
    if (n > 1 && !dataLoaded) return;
    setStep(n);
  };

  const displayValue = (key, type) => {
    const raw = overrideFields[key];
    const local = localDefaults[key];
    const val = raw !== "" ? Number(raw) : local;
    if (val == null) return "Auto-calculated by server";
    return type === "currency" ? formatEGP(val) : formatNum(val);
  };

  const renderFinancialStep = () => (
    <div className="mr-step-content">
      <SectionTitle
        icon={FiTrendingUp}
        title={`Financial Summary — ${monthLabel(month)} ${year}`}
        subtitle="Local values come from Payroll and Expenses. Other fields are auto-calculated on submit unless you override them."
      />

      <div className="mr-metrics-grid">
        <MetricCard
          icon={FiTrendingUp}
          label="Total Revenue"
          value={displayValue("totalRevenue", "currency")}
          accent="green"
          badge={overrideFields.totalRevenue !== "" ? "manual" : "auto"}
        />
        <MetricCard
          icon={FiDollarSign}
          label="Total Expenses"
          value={displayValue("totalExpenses", "currency")}
          sub={`${expenses.length} expense record${expenses.length !== 1 ? "s" : ""}`}
          accent="red"
          badge={overrideFields.totalExpenses !== "" ? "manual" : undefined}
        />
        <MetricCard
          icon={FiDollarSign}
          label="Total Salary Cost"
          value={displayValue("totalSalaryCost", "currency")}
          sub={`${payrolls.length} employee${payrolls.length !== 1 ? "s" : ""} on payroll`}
          accent="gold"
          badge={overrideFields.totalSalaryCost !== "" ? "manual" : undefined}
        />
        <MetricCard
          icon={FiCheckCircle}
          label="Collected Payments"
          value={displayValue("collectedPayments", "currency")}
          accent="green"
          badge={overrideFields.collectedPayments !== "" ? "manual" : "auto"}
        />
        <MetricCard
          icon={FiClock}
          label="Pending Payments"
          value={displayValue("pendingPayments", "currency")}
          accent="orange"
          badge={overrideFields.pendingPayments !== "" ? "manual" : "auto"}
        />
        <MetricCard
          icon={FiUsers}
          label="Active Clients"
          value={displayValue("activeClients", "count")}
          accent="blue"
          badge={overrideFields.activeClients !== "" ? "manual" : undefined}
        />
        <MetricCard
          icon={FiUsers}
          label="Employee Count"
          value={displayValue("employeeCount", "count")}
          accent="blue"
          badge={overrideFields.employeeCount !== "" ? "manual" : undefined}
        />
        <MetricCard
          icon={FiTrendingUp}
          label="Net Profit"
          value={
            effective.netProfit != null
              ? formatEGP(effective.netProfit)
              : "Calculated after submit"
          }
          sub="Revenue − Expenses − Salary Cost"
          accent={effective.netProfit != null && effective.netProfit >= 0 ? "green" : "red"}
        />
      </div>

      <div className="mr-info-box">
        <FiAlertTriangle size={16} />
        <div>
          <strong>Note:</strong> Leave override fields empty and the server will aggregate values from the database.
          Use manual overrides only when you need to replace auto-calculated values.
        </div>
      </div>

      <button
        type="button"
        className={`mr-override-toggle ${showOverrides ? "open" : ""}`}
        onClick={() => setShowOverrides((v) => !v)}
      >
        <FiEdit3 size={15} />
        <span>Override values manually (optional)</span>
        {overrideCount > 0 && <span className="mr-override-count">{overrideCount}</span>}
      </button>

      {showOverrides && (
        <div className="mr-override-panel">
          <div className="mr-override-grid">
            {OVERRIDE_FIELDS.map(({ key, label, type }) => {
              const local = localDefaults[key];
              const hasLocal = local != null;
              return (
                <div key={key} className="mr-override-field">
                  <div className="mr-override-field-head">
                    <label>{label}</label>
                  </div>
                  {hasLocal && (
                    <div className="mr-override-hint">
                      Local value: {type === "currency" ? formatEGP(local) : formatNum(local)}
                    </div>
                  )}
                  <div className="mr-override-input-row">
                    <input
                      type="number"
                      min="0"
                      value={overrideFields[key]}
                      onChange={(e) => updateOverrideField(key, e.target.value)}
                      placeholder={hasLocal ? "Leave empty to use local value" : "Leave empty for auto-calc"}
                    />
                    {overrideFields[key] !== "" && (
                      <button type="button" className="mr-reset-btn" onClick={() => resetOverride(key)} title="Clear override">
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <StepActions onBack={() => setStep(1)} onNext={() => setStep(3)} />
    </div>
  );

  const renderAttendanceStep = () => (
    <div className="mr-step-content">
      <SectionTitle
        icon={FiUsers}
        title="Attendance"
        subtitle="Enter attendance data for each employee on payroll this month"
      />

      {attendance.length === 0 ? (
        <div className="mr-empty-state">
          <FiUsers size={32} />
          <p>No employees on payroll for this month</p>
          <span>You can continue and submit the report without attendance records</span>
        </div>
      ) : (
        <div className="mr-attendance-wrap">
          <table className="mr-attendance-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Work Days</th>
                <th>Absences</th>
                <th>Annual Leave</th>
                <th>Sick Leave</th>
                <th>Late Count</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map((row, i) => (
                <tr key={row.employee || i}>
                  <td>
                    <div className="mr-emp-cell">
                      <span className="mr-emp-avatar">{userName(row.employee).charAt(0)}</span>
                      <span>{userName(row.employee)}</span>
                    </div>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="31"
                      className="mr-num-input"
                      value={row.workDays}
                      onChange={(e) => updateAttendanceField(i, "workDays", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="mr-num-input"
                      value={row.absentDays}
                      onChange={(e) => updateAttendanceField(i, "absentDays", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="mr-num-input"
                      value={row.annualLeaves}
                      onChange={(e) => updateAttendanceField(i, "annualLeaves", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="mr-num-input"
                      value={row.sickLeaves}
                      onChange={(e) => updateAttendanceField(i, "sickLeaves", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      className="mr-num-input"
                      value={row.lateCount}
                      onChange={(e) => updateAttendanceField(i, "lateCount", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <input
                      className="mr-notes-input"
                      value={row.notes || ""}
                      placeholder="—"
                      onChange={(e) => updateAttendanceField(i, "notes", e.target.value || null)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <StepActions onBack={() => setStep(2)} onNext={() => setStep(4)} />
    </div>
  );

  const renderNotesStep = () => (
    <div className="mr-step-content">
      <SectionTitle
        icon={FiFileText}
        title="Management Notes"
        subtitle="Risks, decisions needed, and recommendations for next month"
      />

      <div className="mr-notes-grid">
        <div className="modal-field">
          <label>Risks & Decisions Needed</label>
          <textarea
            rows={5}
            value={risksAndDecisions}
            onChange={(e) => setRisksAndDecisions(e.target.value)}
            placeholder="e.g. Delayed invoice collection for Client X — needs follow-up"
          />
        </div>
        <div className="modal-field">
          <label>Recommendations</label>
          <textarea
            rows={5}
            value={recommendations}
            onChange={(e) => setRecommendations(e.target.value)}
            placeholder="e.g. Increase ad budget for next month"
          />
        </div>
      </div>

      <StepActions onBack={() => setStep(3)} onNext={() => setStep(5)} />
    </div>
  );

  const renderReviewStep = () => (
    <div className="mr-step-content">
      <SectionTitle
        icon={FiCheckCircle}
        title={`Review & Submit — ${monthLabel(month)} ${year}`}
        subtitle="Confirm all data before generating the report"
      />

      <div className="mr-review-section">
        <h5>Financial Data</h5>
        <div className="mr-review-grid">
          {OVERRIDE_FIELDS.map(({ key, label, type }) => (
            <div key={key} className="mr-review-item">
              <span>{label}</span>
              <strong>{displayValue(key, type)}</strong>
            </div>
          ))}
          <div className="mr-review-item highlight">
            <span>Net Profit</span>
            <strong>{effective.netProfit != null ? formatEGP(effective.netProfit) : "Calculated after submit"}</strong>
          </div>
        </div>
      </div>

      <div className="mr-review-section">
        <h5>Attendance ({attendance.length} employee{attendance.length !== 1 ? "s" : ""})</h5>
        {attendance.length === 0 ? (
          <p className="mr-review-empty">No attendance records</p>
        ) : (
          <div className="mr-review-attendance">
            {attendance.map((row, i) => (
              <div key={row.employee || i} className="mr-review-att-row">
                <span className="mr-review-att-name">{userName(row.employee)}</span>
                <span>Work: {row.workDays}</span>
                <span>Absent: {row.absentDays}</span>
                <span>Annual: {row.annualLeaves}</span>
                <span>Sick: {row.sickLeaves}</span>
                <span>Late: {row.lateCount}</span>
                {row.notes && <span className="mr-review-att-notes">{row.notes}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {(risksAndDecisions || recommendations) && (
        <div className="mr-review-section">
          <h5>Notes</h5>
          {risksAndDecisions && (
            <div className="review-notes">
              <b>Risks & Decisions:</b> {risksAndDecisions}
            </div>
          )}
          {recommendations && (
            <div className="review-notes">
              <b>Recommendations:</b> {recommendations}
            </div>
          )}
        </div>
      )}

      {overrideCount > 0 && (
        <div className="mr-info-box warning">
          <FiEdit3 size={16} />
          <div>
            {overrideCount} field{overrideCount !== 1 ? "s" : ""} manually overridden — custom values will be sent instead of auto-calculated ones.
          </div>
        </div>
      )}

      <StepActions
        onBack={() => setStep(4)}
        onNext={handleSubmit}
        nextLabel={submitting ? "Generating…" : "Generate Report"}
        nextDisabled={submitting}
        nextPrimary
      />
    </div>
  );

  return (
    <div className="report-page">
      <div className="report-header">
        <div>
          <h1 className="report-title">Monthly Report</h1>
          <p className="report-subtitle">Generate the management report for a given month</p>
        </div>
      </div>

      <div className="report-stepper">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const done = dataLoaded && step > s.n;
          const active = step === s.n;
          const disabled = s.n > 1 && !dataLoaded;
          return (
            <div key={s.n} className="mr-stepper-item">
              <button
                type="button"
                className={`step-pill ${active ? "active" : ""} ${done ? "done" : ""} ${disabled ? "disabled" : ""}`}
                onClick={() => goToStep(s.n)}
              >
                <span className="step-num">{done ? "✓" : s.n}</span>
                <Icon size={14} />
                {s.label}
              </button>
              {idx < STEPS.length - 1 && <span className="mr-stepper-line" />}
            </div>
          );
        })}
      </div>

      <div className="report-card">
        {step === 1 && (
          <div className="mr-step-content">
            <SectionTitle
              icon={FiCalendar}
              title="Select Month & Year"
              subtitle="Payroll and expense data will be loaded for the selected period"
            />

            <div className="mr-period-picker">
              <div className="modal-field">
                <label>Month</label>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-field">
                <label>Year</label>
                <input
                  type="number"
                  min="2020"
                  max="2100"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                />
              </div>
            </div>

            {existingReportWarning && (
              <div className="mr-info-box warning">
                <FiAlertTriangle size={16} />
                <div>A report already exists for this month. Delete it first before creating a new one.</div>
              </div>
            )}

            <button className="primary-btn mr-load-btn" onClick={handleLoadData} disabled={loadingData}>
              {loadingData ? "Loading…" : "Load Data & Continue"}
              {!loadingData && <FiChevronRight size={16} />}
            </button>
          </div>
        )}

        {step === 2 && renderFinancialStep()}
        {step === 3 && renderAttendanceStep()}
        {step === 4 && renderNotesStep()}
        {step === 5 && renderReviewStep()}
      </div>

      <div className="past-reports">
        <SectionTitle
          icon={FiFileText}
          title="Past Reports"
          subtitle={`${pastReports.length} report${pastReports.length !== 1 ? "s" : ""}`}
        />

        {loadingReports ? (
          <div className="empty-note">Loading…</div>
        ) : pastReports.length === 0 ? (
          <div className="mr-empty-state small">
            <FiFileText size={24} />
            <p>No reports yet</p>
          </div>
        ) : (
          <div className="mr-past-grid">
            {pastReports
              .slice()
              .sort((a, b) => b.year - a.year || b.month - a.month)
              .map((r) => (
                <div key={r._id} className="mr-past-card" onClick={() => setViewingReport(r)}>
                  <div className="mr-past-card-head">
                    <span className="report-list-month">{monthLabel(r.month)} {r.year}</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="del-btn" onClick={(e) => handleEditReport(r, e)} title="Edit">
                        <FiEdit3 size={16} />
                      </button>
                      <button className="del-btn" onClick={(e) => handleDeleteReport(r._id, e)} title="Delete">
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="mr-past-card-stats">
                    <span>Revenue: {formatEGP(r.totalRevenue)}</span>
                    <span>Expenses: {formatEGP(r.totalExpenses)}</span>
                    <span>Salaries: {formatEGP(r.totalSalaryCost)}</span>
                  </div>
                  {r.netProfit != null && (
                    <div className={`mr-past-profit ${r.netProfit >= 0 ? "positive" : "negative"}`}>
                      Net Profit: {formatEGP(r.netProfit)}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      {viewingReport && (
        <div className="modal-overlay" onClick={() => setViewingReport(null)}>
          <div className="modal mr-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{monthLabel(viewingReport.month)} {viewingReport.year} Report</h3>
              <button type="button" className="modal-close" onClick={() => setViewingReport(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="mr-view-section">
                <h5>Financial Data</h5>
                <div className="preview-grid">
                  <div className="preview-item">
                    <div className="preview-label">Total Revenue</div>
                    <div className="preview-value accent-green">{formatEGP(viewingReport.totalRevenue)}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Total Expenses</div>
                    <div className="preview-value accent-red">{formatEGP(viewingReport.totalExpenses)}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Total Salary Cost</div>
                    <div className="preview-value accent-gold">{formatEGP(viewingReport.totalSalaryCost)}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Collected Payments</div>
                    <div className="preview-value">{formatEGP(viewingReport.collectedPayments)}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Pending Payments</div>
                    <div className="preview-value">{formatEGP(viewingReport.pendingPayments)}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Active Clients</div>
                    <div className="preview-value">{viewingReport.activeClients ?? "—"}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Employee Count</div>
                    <div className="preview-value">{viewingReport.employeeCount ?? "—"}</div>
                  </div>
                  <div className="preview-item">
                    <div className="preview-label">Net Profit</div>
                    <div className={`preview-value ${(viewingReport.netProfit ?? 0) >= 0 ? "accent-green" : "accent-red"}`}>
                      {formatEGP(viewingReport.netProfit)}
                    </div>
                  </div>
                </div>
              </div>

              {Array.isArray(viewingReport.attendance) && viewingReport.attendance.length > 0 && (
                <div className="mr-view-section">
                  <h5>Attendance ({viewingReport.attendance.length})</h5>
                  <div className="mr-attendance-wrap compact">
                    <table className="mr-attendance-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Work</th>
                          <th>Absent</th>
                          <th>Annual</th>
                          <th>Sick</th>
                          <th>Late</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingReport.attendance.map((row, i) => (
                          <tr key={row.employee || i}>
                            <td>{userName(row.employee)}</td>
                            <td>{row.workDays}</td>
                            <td>{row.absentDays}</td>
                            <td>{row.annualLeaves}</td>
                            <td>{row.sickLeaves}</td>
                            <td>{row.lateCount}</td>
                            <td>{row.notes || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(viewingReport.risksAndDecisions || viewingReport.recommendations) && (
                <div className="mr-view-section">
                  <h5>Notes</h5>
                  {viewingReport.risksAndDecisions && (
                    <div className="review-notes">
                      <b>Risks & Decisions:</b> {viewingReport.risksAndDecisions}
                    </div>
                  )}
                  {viewingReport.recommendations && (
                    <div className="review-notes">
                      <b>Recommendations:</b> {viewingReport.recommendations}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingReport && (
        <div className="modal-overlay" onClick={() => setEditingReport(null)}>
          <div className="modal mr-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {monthLabel(editingReport.month)} {editingReport.year} Report</h3>
              <button type="button" className="modal-close" onClick={() => setEditingReport(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="mr-view-section">
                <h5>Financial Data</h5>
                <div className="mr-override-grid">
                  {OVERRIDE_FIELDS.map(({ key, label }) => (
                    <div key={key} className="mr-override-field">
                      <label>{label}</label>
                      <input
                        type="number"
                        min="0"
                        value={editingReport[key] ?? ""}
                        onChange={(e) => setEditingReport(prev => ({ ...prev, [key]: e.target.value ? Number(e.target.value) : null }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mr-view-section">
                <h5>Notes</h5>
                <div className="modal-field">
                  <label>Risks & Decisions</label>
                  <textarea
                    rows={3}
                    value={editingReport.risksAndDecisions || ""}
                    onChange={(e) => setEditingReport(prev => ({ ...prev, risksAndDecisions: e.target.value }))}
                  />
                </div>
                <div className="modal-field">
                  <label>Recommendations</label>
                  <textarea
                    rows={3}
                    value={editingReport.recommendations || ""}
                    onChange={(e) => setEditingReport(prev => ({ ...prev, recommendations: e.target.value }))}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setEditingReport(null)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleUpdateReport} disabled={submitting}>
                  {submitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepActions({ onBack, onNext, nextLabel = "Continue", nextDisabled = false, nextPrimary = false }) {
  return (
    <div className="step-actions">
      <button type="button" className="secondary-btn" onClick={onBack}>
        <FiChevronLeft size={16} />
        Back
      </button>
      <button
        type="button"
        className={nextPrimary ? "primary-btn" : "primary-btn"}
        onClick={onNext}
        disabled={nextDisabled}
      >
        {nextLabel}
        {!nextDisabled && nextLabel === "Continue" && <FiChevronRight size={16} />}
      </button>
    </div>
  );
}
